/**
 * Share View Page
 *
 * Main page for viewing shared posts. Extracts PostKey from URL fragment,
 * fetches encrypted data, and decrypts client-side.
 */

import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchPublicShare, downloadEncryptedAsset, type ShareError, type PublicShareResponse } from '../api/fetchShare';
import { decryptCaption, decryptAssetDetail, unwrapAssetCEK, decryptAssetSingle, decryptAssetSegmented, type CaptionData } from '../crypto/decrypt';
import { base64urlToUint8Array } from '../utils/base64url';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import MediaGallery from '../components/MediaGallery';
import AppDownloadCTA from '../components/AppDownloadCTA';

type ErrorType = 'no_key' | 'not_found' | 'revoked' | 'decrypt_failed' | 'network' | 'unknown';

interface MediaItem {
  id: string;
  kind: 'image' | 'video';
  mime: string;
  dataUrl: string;
  width: number | null;
  height: number | null;
  /** Optional per-photo detail, decrypted. Null when the photo has none. */
  detail: string | null;
}

export default function ShareViewPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const location = useLocation();
  const { t } = useTranslation('share');
  const { t: tc } = useTranslation('common');
  const localizedPath = useLocalizedPath();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ type: ErrorType; message?: string } | null>(null);
  const [caption, setCaption] = useState<CaptionData | null>(null);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  /** Which photo the gallery is showing, so its detail can be rendered below. */
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [ownerName, setOwnerName] = useState<string>('');
  const [ownerAvatar, setOwnerAvatar] = useState<string | null>(null);
  const [decryptingAssets, setDecryptingAssets] = useState(true);

  useEffect(() => {
    loadShare();
  }, [shareId, location.hash]);

  const loadShare = async () => {
    if (!shareId) {
      setError({ type: 'not_found' });
      setLoading(false);
      return;
    }

    // Extract PostKey from URL fragment (#k=...)
    const hash = location.hash;
    const keyMatch = hash.match(/^#k=(.+)$/);

    if (!keyMatch) {
      setError({ type: 'no_key' });
      setLoading(false);
      return;
    }

    let postKey: Uint8Array;
    try {
      postKey = base64urlToUint8Array(keyMatch[1]);
      if (postKey.length !== 32) {
        throw new Error('Invalid key length');
      }
    } catch {
      setError({ type: 'no_key', message: 'Invalid encryption key in URL' });
      setLoading(false);
      return;
    }

    try {
      // Fetch encrypted data from server
      const data = await fetchPublicShare(shareId);

      // Set owner info
      setOwnerName(data.post.owner.name);
      setOwnerAvatar(data.post.owner.profile_picture_url);

      // Decrypt caption
      try {
        const decryptedCaption = decryptCaption(
          data.post.caption_ct,
          data.post.caption_nonce,
          postKey,
          data.post.id
        );
        setCaption(decryptedCaption);
      } catch (err) {
        console.error('Failed to decrypt caption:', err);
        setError({ type: 'decrypt_failed' });
        setLoading(false);
        return;
      }

      setLoading(false);

      // Decrypt assets in background
      await decryptAssets(data, postKey);

    } catch (err) {
      const shareError = err as ShareError;
      if (shareError.type) {
        setError({ type: shareError.type, message: shareError.message });
      } else {
        setError({ type: 'unknown', message: String(err) });
      }
      setLoading(false);
    }
  };

  const decryptAssets = async (data: PublicShareResponse, postKey: Uint8Array) => {
    setDecryptingAssets(true);

    // Filter to only images and videos (skip thumbnails and audio for now)
    const visualAssets = data.assets
      .filter(a => a.kind === 'image' || a.kind === 'video')
      .sort((a, b) => a.display_order - b.display_order);

    const decryptedItems: MediaItem[] = [];

    for (const asset of visualAssets) {
      try {
        // Use full image URL for best quality
        const downloadUrl = asset.download_url;

        // Download encrypted asset
        const ciphertext = await downloadEncryptedAsset(downloadUrl);

        // Unwrap CEK
        const cek = unwrapAssetCEK(
          asset.cek_wrap,
          asset.cek_wrap_nonce,
          postKey,
          data.post.id,
          asset.id
        );

        // Decrypt based on scheme
        let plaintext: Uint8Array;
        if (asset.nonce_scheme === 'segmented' && asset.segment_size) {
          plaintext = decryptAssetSegmented(
            ciphertext,
            asset.asset_nonce,
            cek,
            asset.segment_size
          );
        } else {
          plaintext = decryptAssetSingle(
            ciphertext,
            asset.asset_nonce,
            cek
          );
        }

        // Create blob URL for display - slice to get a proper ArrayBuffer
        const blob = new Blob([plaintext.slice().buffer], { type: asset.mime });
        const dataUrl = URL.createObjectURL(blob);

        // Optional per-photo detail. Failing to read one must never cost us the
        // photo itself, so it gets its own try/catch inside the asset's.
        let detail: string | null = null;
        if (asset.detail_ct && asset.detail_nonce) {
          try {
            detail = decryptAssetDetail(
              asset.detail_ct,
              asset.detail_nonce,
              postKey,
              data.post.id,
              asset.id
            ).text;
          } catch (err) {
            console.error(`Failed to decrypt detail for asset ${asset.id}:`, err);
          }
        }

        decryptedItems.push({
          id: asset.id,
          kind: asset.kind as 'image' | 'video',
          mime: asset.mime,
          dataUrl,
          width: asset.width,
          height: asset.height,
          detail,
        });

        // Update state incrementally so images appear as they decrypt
        setMediaItems([...decryptedItems]);

      } catch (err) {
        console.error(`Failed to decrypt asset ${asset.id}:`, err);
        // Continue with other assets
      }
    }

    setDecryptingAssets(false);
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      mediaItems.forEach(item => {
        URL.revokeObjectURL(item.dataUrl);
      });
    };
  }, []);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState type={error.type} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
      {/* Animated background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-waffle-orange/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-amber-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-orange-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-950/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <a href={localizedPath('/')} className="flex items-center gap-2 group">
              <img src="/logo.svg" alt="Family Waffle" className="w-7 h-7 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform" />
              <span className="text-base sm:text-lg font-semibold text-white">Family Waffle</span>
            </a>

            {/* Download buttons in header */}
            <div className="flex gap-2">
              <a
                href="https://apps.apple.com/app/family-waffle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-all min-h-[44px] min-w-[44px]"
                title="Download on App Store"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span className="hidden sm:inline text-xs font-medium">{t('shareView.ios')}</span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.familywaffle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-all min-h-[44px] min-w-[44px]"
                title="Get it on Google Play"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <span className="hidden sm:inline text-xs font-medium">{t('shareView.android')}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="relative py-4 sm:py-6 lg:py-8 px-4">
        <div className="max-w-lg mx-auto space-y-4 sm:space-y-6">
          {/* Owner info */}
          <div className="rounded-2xl p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-xl">
            <div className="flex items-center gap-3">
              {ownerAvatar ? (
                <img
                  src={ownerAvatar}
                  alt={ownerName}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ring-2 ring-waffle-orange/30"
                />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-waffle-orange to-amber-500 flex items-center justify-center text-white font-semibold text-base sm:text-lg">
                  {ownerName.charAt(0).toUpperCase()}
                </div>
              )}
              <p className="font-semibold text-white text-base sm:text-lg">{ownerName}</p>
            </div>

            {/* Date/Location Row - matching iOS PostDetailSheet */}
            {caption && (caption.date || caption.place) && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                {/* Date on left */}
                {caption.date && (
                  <div className="flex items-center gap-1.5 text-gray-400 text-xs sm:text-sm">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {new Date(caption.date).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Location on right */}
                {caption.place && (
                  <div className="flex items-center gap-1.5 text-gray-400 text-xs sm:text-sm">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {caption.place}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Media Gallery */}
          {mediaItems.length > 0 && (
            <MediaGallery items={mediaItems} onIndexChange={setCurrentMediaIndex} />
          )}

          {/* Loading indicator for assets */}
          {decryptingAssets && mediaItems.length === 0 && (
            <div className="rounded-2xl bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 aspect-square flex items-center justify-center min-h-[200px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-waffle-orange border-t-transparent mb-2 mx-auto"></div>
                <p className="text-xs sm:text-sm text-gray-400">{t('shareView.decryptingMedia')}</p>
              </div>
            </div>
          )}

          {/* Detail of the currently visible photo — sits between the media and the
              post title, matching the native apps. Renders nothing when absent. */}
          {mediaItems[currentMediaIndex]?.detail && (
            <div className="rounded-2xl p-3 sm:p-4 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20">
              <p className="text-gray-100 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                {mediaItems[currentMediaIndex]!.detail}
              </p>
            </div>
          )}

          {/* Caption text */}
          {caption && caption.text && (
            <div className="rounded-2xl p-4 sm:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-xl">
              <p className="text-white text-base sm:text-lg leading-relaxed whitespace-pre-wrap">
                {caption.text}
              </p>
            </div>
          )}

          {/* App Download CTA */}
          <AppDownloadCTA />

          {/* Footer */}
          <footer className="text-center text-xs sm:text-sm text-gray-400 pt-6 sm:pt-8 pb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src="/logo.svg" alt="Family Waffle" className="w-5 h-5 sm:w-6 sm:h-6" />
              <p className="text-gray-300 text-sm sm:text-base">{tc('sharedFooter.sharedWith')}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p>{tc('sharedFooter.encrypted')}</p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
